import React, { useState, useEffect, useCallback } from "react";
import {
  MessageSquare,
  CheckCheck,
  Clock,
  XCircle,
  TrendingUp,
  Users,
  Send,
  Eye,
  RefreshCw,
  Search,
  X,
  Calendar,
  Phone,
  AlertCircle,
} from "lucide-react";
import axios from "axios";
import toast from "react-hot-toast";

interface MessageStats {
  total: number;
  delivered: number;
  read: number;
  failed: number;
  deliveryRate: string;
  readRate: string;
  failureRate: string;
  breakdown: Array<{
    status: string;
    count: number;
  }>;
}

interface WhatsAppMessage {
  _id: string;
  messageId: string;
  recipient: string;
  status: string;
  messageType: string;
  timestamps: {
    queued?: string;
    sent?: string;
    delivered?: string;
    read?: string;
    failed?: string;
  };
  webhookEvents?: Array<{
    status: string;
    timestamp: string;
    eventData?: any;
  }>;
  metadata?: {
    studentId?: string;
    recipientType?: string;
  };
  error?: {
    code?: number;
    title?: string;
    message?: string;
    details?: string;
  };
  createdAt: string;
}

interface MessageTimeline {
  messageId: string;
  recipient: string;
  currentStatus: string;
  timeline: Array<{
    status: string;
    timestamp: string;
  }>;
}

const WhatsAppDashboard: React.FC = () => {
  const [stats, setStats] = useState<MessageStats | null>(null);
  const [recentMessages, setRecentMessages] = useState<WhatsAppMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedMessage, setSelectedMessage] =
    useState<WhatsAppMessage | null>(null);
  const [messageTimeline, setMessageTimeline] =
    useState<MessageTimeline | null>(null);
  const [showTimelineModal, setShowTimelineModal] = useState(false);
  const [searchPhone, setSearchPhone] = useState("");
  const [filter, setFilter] = useState({
    messageType: "",
    status: "",
    startDate: "",
    endDate: "",
  });

  // Fetch statistics from webhook stats endpoint
  const fetchStats = useCallback(
    async (showToast = false) => {
      try {
        const params = new URLSearchParams();
        if (filter.startDate) params.append("startDate", filter.startDate);
        if (filter.endDate) params.append("endDate", filter.endDate);
        if (filter.messageType)
          params.append("messageType", filter.messageType);

        const apiUrl =
          import.meta.env.VITE_BACKEND_URL || "http://localhost:5000";
        const response = await axios.get(
          `${apiUrl}/api/whatsapp/webhook/stats?${params}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );

        if (response.data.success) {
          setStats(response.data.stats);
          if (showToast) {
            toast.success("Statistics updated");
          }
        }
      } catch (error) {
        console.error("Error fetching WhatsApp stats:", error);
        toast.error("Failed to load WhatsApp statistics");
      }
    },
    [filter.startDate, filter.endDate, filter.messageType],
  );

  // Fetch recent messages
  const fetchRecentMessages = useCallback(
    async (showToast = false) => {
      try {
        setLoading(true);

        const apiUrl =
          import.meta.env.VITE_BACKEND_URL || "http://localhost:5000";
        let url = `${apiUrl}/api/whatsapp/messages/recent`;
        const params: any = {
          limit: 100,
        };

        if (filter.status) params.status = filter.status;
        if (filter.messageType) params.messageType = filter.messageType;

        // If searching by phone, use the recipient endpoint instead
        if (searchPhone) {
          const cleanPhone = searchPhone.replace(/\+/g, "");
          url = `${apiUrl}/api/whatsapp/webhook/recipient/${cleanPhone}`;
          if (filter.status) params.status = filter.status;
        }

        const response = await axios.get(url, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          params,
        });

        if (response.data.success) {
          setRecentMessages(response.data.messages || []);
          if (showToast) {
            toast.success(
              `Loaded ${response.data.messages?.length || 0} messages`,
            );
          }
        }
      } catch (error: any) {
        console.error("Error fetching recent messages:", error);
        if (error.response?.status === 404) {
          toast.error("No messages found for this recipient");
          setRecentMessages([]);
        } else {
          toast.error(error.response?.data?.error || "Failed to load messages");
        }
      } finally {
        setLoading(false);
      }
    },
    [filter.status, filter.messageType, searchPhone],
  );

  // Fetch message timeline using webhook message endpoint
  const fetchMessageTimeline = async (messageId: string) => {
    try {
      const apiUrl =
        import.meta.env.VITE_BACKEND_URL || "http://localhost:5000";
      const response = await axios.get(
        `${apiUrl}/api/whatsapp/webhook/message/${messageId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );

      if (response.data.success) {
        setMessageTimeline(response.data.timeline);
      }
    } catch (error) {
      console.error("Error fetching message timeline:", error);
      toast.error("Failed to load message timeline");
    }
  };

  // Handle refresh
  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchStats(true), fetchRecentMessages(true)]);
    setRefreshing(false);
  };

  // Handle view message details
  const handleViewMessage = async (message: WhatsAppMessage) => {
    setSelectedMessage(message);
    setShowTimelineModal(true);
    await fetchMessageTimeline(message.messageId);
  };

  // Handle search by phone
  const handlePhoneSearch = () => {
    if (searchPhone) {
      fetchRecentMessages(false);
    }
  };

  // Clear phone search
  const handleClearSearch = () => {
    setSearchPhone("");
  };

  // Initial load
  useEffect(() => {
    fetchStats();
    fetchRecentMessages();
  }, [fetchStats, fetchRecentMessages]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchStats(false);
      fetchRecentMessages(false);
    }, 30000);

    return () => clearInterval(interval);
  }, [fetchStats, fetchRecentMessages]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "delivered":
        return "bg-gradient-to-r from-emerald-300 to-green-400 text-white";
      case "read":
        return "bg-gradient-to-r from-blue-300 to-indigo-400 text-white";
      case "sent":
        return "bg-gradient-to-r from-amber-300 to-orange-400 text-white";
      case "failed":
        return "bg-gradient-to-r from-red-300 to-rose-400 text-white";
      case "queued":
        return "bg-gradient-to-r from-slate-300 to-gray-400 text-white";
      default:
        return "bg-gradient-to-r from-slate-300 to-gray-400 text-white";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "delivered":
        return <CheckCheck className="w-4 h-4" />;
      case "read":
        return <Eye className="w-4 h-4" />;
      case "sent":
        return <Send className="w-4 h-4" />;
      case "failed":
        return <XCircle className="w-4 h-4" />;
      case "queued":
        return <Clock className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const getMessageTypeColor = (type: string) => {
    switch (type) {
      case "invoice":
        return "bg-gradient-to-r from-purple-300 to-indigo-400 text-white";
      case "attendance":
        return "bg-gradient-to-r from-blue-300 to-indigo-400 text-white";
      case "notice":
        return "bg-gradient-to-r from-amber-300 to-orange-400 text-white";
      case "payment_confirmation":
        return "bg-gradient-to-r from-emerald-300 to-green-400 text-white";
      case "admission":
        return "bg-gradient-to-r from-indigo-300 to-purple-400 text-white";
      default:
        return "bg-gradient-to-r from-slate-300 to-gray-400 text-white";
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="bg-gradient-to-br from-emerald-300 to-green-400 rounded-2xl p-4 shadow-lg">
                <MessageSquare className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-4xl font-bold text-slate-900">
                  WhatsApp Communications
                </h1>
                <p className="text-slate-600 font-semibold mt-1">
                  Monitor message delivery and engagement
                </p>
              </div>
            </div>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="bg-white border-2 border-slate-200 rounded-xl px-6 py-3 font-bold text-slate-700 hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw
                className={`w-5 h-5 ${refreshing ? "animate-spin" : ""}`}
              />
              {refreshing ? "Refreshing..." : "Refresh"}
            </button>
          </div>
        </div>

        {/* Statistics Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 hover:scale-105 transition-all duration-300">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-gradient-to-br from-blue-300 to-indigo-400 rounded-2xl shadow-lg">
                  <MessageSquare className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-sm font-bold text-slate-600">
                    Total Messages
                  </p>
                  <p className="text-3xl font-bold text-slate-900">
                    {stats.total}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 hover:scale-105 transition-all duration-300">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-gradient-to-br from-emerald-300 to-green-400 rounded-2xl shadow-lg">
                  <CheckCheck className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-sm font-bold text-slate-600">
                    Delivery Rate
                  </p>
                  <p className="text-3xl font-bold text-slate-900">
                    {stats.deliveryRate}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 hover:scale-105 transition-all duration-300">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-gradient-to-br from-blue-300 to-indigo-400 rounded-2xl shadow-lg">
                  <Eye className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-sm font-bold text-slate-600">Read Rate</p>
                  <p className="text-3xl font-bold text-slate-900">
                    {stats.readRate}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 hover:scale-105 transition-all duration-300">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-gradient-to-br from-red-300 to-rose-400 rounded-2xl shadow-lg">
                  <XCircle className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-sm font-bold text-slate-600">
                    Failure Rate
                  </p>
                  <p className="text-3xl font-bold text-slate-900">
                    {stats.failureRate}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 mb-8">
          <h2 className="text-xl font-bold text-slate-900 mb-6">
            Filters & Search
          </h2>

          {/* Phone Search */}
          <div className="mb-6">
            <label className="block text-sm font-bold text-slate-900 mb-2">
              Search by Phone Number
            </label>
            <div className="flex gap-3">
              <div className="flex-1 relative">
                <input
                  type="text"
                  value={searchPhone}
                  onChange={(e) => setSearchPhone(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && handlePhoneSearch()}
                  placeholder="e.g., 254797945600"
                  className="w-full pl-12 pr-12 py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200/60 rounded-xl font-semibold text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                />
                {searchPhone && (
                  <button
                    onClick={handleClearSearch}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>
              <button
                onClick={handlePhoneSearch}
                disabled={!searchPhone}
                className="bg-gradient-to-r from-emerald-300 to-green-400 text-white rounded-xl px-6 py-3 font-bold hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Search className="w-5 h-5" />
                Search
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-bold text-slate-900 mb-2">
                Message Type
              </label>
              <select
                value={filter.messageType}
                onChange={(e) =>
                  setFilter({ ...filter, messageType: e.target.value })
                }
                className="w-full px-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200/60 rounded-xl font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
              >
                <option value="">All Types</option>
                <option value="invoice">Invoice</option>
                <option value="attendance">Attendance</option>
                <option value="notice">Notice</option>
                <option value="payment_confirmation">
                  Payment Confirmation
                </option>
                <option value="admission">Admission</option>
                <option value="general">General</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-bold text-slate-900 mb-2">
                Status
              </label>
              <select
                value={filter.status}
                onChange={(e) =>
                  setFilter({ ...filter, status: e.target.value })
                }
                className="w-full px-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200/60 rounded-xl font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
              >
                <option value="">All Statuses</option>
                <option value="queued">Queued</option>
                <option value="sent">Sent</option>
                <option value="delivered">Delivered</option>
                <option value="read">Read</option>
                <option value="failed">Failed</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-bold text-slate-900 mb-2">
                Start Date
              </label>
              <input
                type="date"
                value={filter.startDate}
                onChange={(e) =>
                  setFilter({ ...filter, startDate: e.target.value })
                }
                className="w-full px-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200/60 rounded-xl font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-slate-900 mb-2">
                End Date
              </label>
              <input
                type="date"
                value={filter.endDate}
                onChange={(e) =>
                  setFilter({ ...filter, endDate: e.target.value })
                }
                className="w-full px-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200/60 rounded-xl font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Status Breakdown */}
        {stats && stats.breakdown && (
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 mb-8">
            <h2 className="text-xl font-bold text-slate-900 mb-6">
              Status Breakdown
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {stats.breakdown.map((item) => (
                <div
                  key={item.status}
                  className="flex items-center gap-3 p-4 bg-gradient-to-r from-slate-50 to-blue-50 rounded-xl hover:scale-105 transition-all duration-300"
                >
                  <div
                    className={`p-2 rounded-xl shadow-md ${getStatusColor(item.status)}`}
                  >
                    {getStatusIcon(item.status)}
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-600 capitalize">
                      {item.status}
                    </p>
                    <p className="text-2xl font-bold text-slate-900">
                      {item.count}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent Messages Table */}
        {loading ? (
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-16">
            <div className="flex flex-col items-center justify-center">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-slate-200 border-t-blue-500 mb-6"></div>
              <span className="text-xl font-bold text-slate-900">
                Loading messages...
              </span>
            </div>
          </div>
        ) : (
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl overflow-hidden">
            <div className="px-6 py-4 bg-gradient-to-r from-slate-50 to-blue-50 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-900">
                Recent Messages ({recentMessages.length})
              </h2>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-gradient-to-r from-slate-50 to-blue-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-bold text-slate-900 uppercase tracking-wider">
                      Recipient
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-slate-900 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-slate-900 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-slate-900 uppercase tracking-wider">
                      Sent
                    </th>

                    <th className="px-6 py-4 text-left text-xs font-bold text-slate-900 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-200">
                  {recentMessages.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-16 text-center">
                        <div className="flex flex-col items-center">
                          <div className="bg-gradient-to-br from-slate-300 to-gray-400 rounded-2xl p-8 shadow-lg inline-block mb-6">
                            <AlertCircle className="w-16 h-16 text-white" />
                          </div>
                          <h3 className="text-2xl font-bold text-slate-900 mb-3">
                            No Messages Found
                          </h3>
                          <p className="text-slate-600 font-semibold">
                            Try adjusting your filters or search criteria
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    recentMessages.map((message) => (
                      <tr
                        key={message._id}
                        className="hover:bg-gradient-to-r hover:from-slate-50 hover:to-blue-50 cursor-pointer transition-all duration-200"
                        onClick={() => handleViewMessage(message)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <Phone className="w-4 h-4 text-slate-400" />
                            <span className="text-sm font-semibold text-slate-900">
                              {message.recipient}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex px-3 py-1 text-xs font-bold rounded-xl shadow-md ${getMessageTypeColor(
                              message.messageType,
                            )}`}
                          >
                            {message.messageType}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center gap-1 px-3 py-1 text-xs font-bold rounded-xl shadow-md ${getStatusColor(
                              message.status,
                            )}`}
                          >
                            {getStatusIcon(message.status)}
                            {message.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-slate-900">
                          {message.timestamps.sent
                            ? new Date(message.timestamps.sent).toLocaleString()
                            : "-"}
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewMessage(message);
                            }}
                            className="text-blue-600 hover:text-blue-800 font-bold flex items-center gap-1 hover:scale-110 transition-all duration-300"
                          >
                            <Eye className="w-4 h-4" />
                            View
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Message Timeline Modal */}
        {showTimelineModal && selectedMessage && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              {/* Header */}
              <div className="p-6 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-blue-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="bg-gradient-to-br from-emerald-300 to-green-400 rounded-2xl p-3 shadow-lg">
                      <MessageSquare className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-slate-900">
                        Message Timeline
                      </h2>
                      <p className="text-sm font-semibold text-slate-600 mt-1 font-mono">
                        {selectedMessage.messageId}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setShowTimelineModal(false);
                      setSelectedMessage(null);
                      setMessageTimeline(null);
                    }}
                    className="p-2 bg-white border-2 border-slate-200 rounded-xl hover:shadow-lg hover:scale-110 transition-all duration-300"
                  >
                    <X className="w-6 h-6 text-slate-400" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                {/* Message Details */}
                <div className="bg-gradient-to-r from-slate-50 to-blue-50 rounded-2xl p-6 mb-6 border border-slate-200">
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <p className="text-sm font-bold text-slate-600 mb-2">
                        Recipient
                      </p>
                      <p className="text-lg font-bold text-slate-900 flex items-center gap-2">
                        <Phone className="w-5 h-5" />
                        {selectedMessage.recipient}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-bold text-slate-600 mb-2">
                        Message Type
                      </p>
                      <span
                        className={`inline-flex items-center px-3 py-1 rounded-xl text-sm font-bold shadow-md ${getMessageTypeColor(
                          selectedMessage.messageType,
                        )}`}
                      >
                        {selectedMessage.messageType}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-bold text-slate-600 mb-2">
                        Current Status
                      </p>
                      <span
                        className={`inline-flex items-center gap-1 px-3 py-1 rounded-xl text-sm font-bold shadow-md ${getStatusColor(
                          selectedMessage.status,
                        )}`}
                      >
                        {getStatusIcon(selectedMessage.status)}
                        {selectedMessage.status}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-bold text-slate-600 mb-2">
                        Created
                      </p>
                      <p className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        {new Date(selectedMessage.createdAt).toLocaleString()}
                      </p>
                    </div>
                  </div>

                  {selectedMessage.metadata && (
                    <div className="mt-6 pt-6 border-t border-slate-200">
                      <p className="text-sm font-bold text-slate-900 mb-3">
                        Metadata
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {selectedMessage.metadata.studentId && (
                          <span className="inline-flex px-3 py-1 text-xs font-bold rounded-xl shadow-md bg-gradient-to-r from-blue-300 to-indigo-400 text-white">
                            Student: {selectedMessage.metadata.studentId}
                          </span>
                        )}
                        {selectedMessage.metadata.recipientType && (
                          <span className="inline-flex px-3 py-1 text-xs font-bold rounded-xl shadow-md bg-gradient-to-r from-purple-300 to-indigo-400 text-white">
                            Type: {selectedMessage.metadata.recipientType}
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {selectedMessage.error && (
                    <div className="mt-6 pt-6 border-t border-slate-200">
                      <div className="flex items-start gap-3 p-4 bg-gradient-to-r from-red-50 to-rose-50 rounded-xl border border-red-200">
                        <AlertCircle className="w-6 h-6 flex-shrink-0 mt-0.5 text-red-600" />
                        <div>
                          <p className="font-bold text-red-900">
                            {selectedMessage.error.title}
                          </p>
                          <p className="text-sm font-semibold text-red-700 mt-1">
                            {selectedMessage.error.message}
                          </p>
                          {selectedMessage.error.details && (
                            <p className="text-xs font-semibold text-red-600 mt-1">
                              {selectedMessage.error.details}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Timeline */}
                {messageTimeline ? (
                  <div>
                    <h3 className="text-xl font-bold text-slate-900 mb-6">
                      Status Timeline
                    </h3>
                    <div className="space-y-4">
                      {messageTimeline.timeline.map((event, index) => (
                        <div key={index} className="flex items-start gap-4">
                          <div
                            className={`p-3 rounded-xl shadow-md ${getStatusColor(
                              event.status,
                            )}`}
                          >
                            {getStatusIcon(event.status)}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <p className="font-bold text-slate-900 capitalize">
                                {event.status}
                              </p>
                              <p className="text-sm font-semibold text-slate-600">
                                {new Date(event.timestamp).toLocaleString()}
                              </p>
                            </div>
                            {index < messageTimeline.timeline.length - 1 && (
                              <div className="h-8 w-0.5 bg-slate-200 ml-6 mt-2"></div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Webhook Events */}
                    {selectedMessage.webhookEvents &&
                      selectedMessage.webhookEvents.length > 0 && (
                        <div className="mt-8 pt-8 border-t border-slate-200">
                          <h3 className="text-xl font-bold text-slate-900 mb-6">
                            Webhook Events (
                            {selectedMessage.webhookEvents.length})
                          </h3>
                          <div className="space-y-3 max-h-60 overflow-y-auto">
                            {selectedMessage.webhookEvents.map(
                              (event, index) => (
                                <div
                                  key={index}
                                  className="bg-gradient-to-r from-slate-50 to-blue-50 rounded-xl p-4 border border-slate-200"
                                >
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="font-bold text-sm text-slate-900 capitalize">
                                      {event.status}
                                    </span>
                                    <span className="text-xs font-semibold text-slate-600">
                                      {new Date(
                                        event.timestamp,
                                      ).toLocaleString()}
                                    </span>
                                  </div>
                                  {event.eventData && (
                                    <pre className="text-xs font-semibold text-slate-700 mt-2 overflow-x-auto bg-white/50 p-3 rounded-lg">
                                      {JSON.stringify(event.eventData, null, 2)}
                                    </pre>
                                  )}
                                </div>
                              ),
                            )}
                          </div>
                        </div>
                      )}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-slate-200 border-t-blue-500 mb-4"></div>
                    <span className="font-bold text-slate-900">
                      Loading timeline...
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WhatsAppDashboard;
